import React from "react";
import PropTypes from "prop-types";
import { Grid, Paper, Typography, Button } from "@material-ui/core";
import { toCamelCase } from "utils/Utils";
import * as XLSX from "xlsx";

const styles = {
  card: {
    border: "1px solid #E5E7EB",
    borderRadius: 12,
    padding: 24,
    marginBottom: 24,
    boxShadow: "none",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
  },
  title: {
    fontSize: 22,
    fontWeight: 600,
    color: "#1E3A5F",
  },
  item: {
    marginBottom: 22,
  },
  label: {
    fontSize: 13,
    color: "#6B7280",
    marginBottom: 6,
    fontWeight: 500,
  },
  value: {
    fontSize: 15,
    fontWeight: 600,
    color: "#1F2937",
    wordBreak: "break-word",
  },
};

const Field = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={4}>
    <div style={styles.item}>
      <Typography style={styles.label}>
        {label}
      </Typography>

      <Typography style={styles.value}>
        {value || "NA"}
      </Typography>
    </div>
  </Grid>
);

const BankDetails = ({ bankDetails, bankAnalyzerDetails }) => {
  const downloadBankStatementExcel = async () => {
    try {
      const data = await bankAnalyzerDetails;
      const workbook = XLSX.utils.book_new();
      Object.entries(data || {}).forEach(([bankName, bankAccounts]) => {
        // Handle both array and object response
        const accounts = Array.isArray(bankAccounts)
          ? bankAccounts
          : bankAccounts
          ? [bankAccounts]
          : [];
        accounts.forEach((account, accountIndex) => {
          const accountData =
            account?.decrypted_data?.["aa:Account"] || {};

          const profile = accountData?.["aa:Profile"] || {};
          const holder =
            profile?.["aa:Holders"]?.["aa:Holder"] || {};
          const summary =
            accountData?.["aa:Summary"] || {};
          const transactionsData =
            accountData?.["aa:Transactions"] || {};
          const transactionList =
            transactionsData?.["aa:Transaction"] || [];
          const transactions = Array.isArray(transactionList)
            ? transactionList
            : transactionList
            ? [transactionList]
            : [];
          const accountNumber =
            account?.masked_account ||
            accountData?.maskedAccNumber ||
            "";

          const ifsc =
            summary?.ifsc ||
            accountData?.ifsc ||
            "";

          const branchName =
            summary?.branch ||
            summary?.branchName ||
            accountData?.branch ||
            "";

          const branchAddress =
            summary?.branchAddress ||
            summary?.address ||
            accountData?.branchAddress ||
            "";

          const accountType =
            summary?.type ||
            summary?.accountType ||
            accountData?.accountType ||
            "";
          const transactionRows = transactions.map((transaction) => {
            const amount = Number(transaction?.amount || 0);

            const type =
              String(transaction?.type || "").toUpperCase();

            return {
              Date: transaction?.transactionTimestamp || "",
              "Value Date": transaction?.valueDate || "",
              Description: transaction?.narration || "",
              Reference: transaction?.reference || "",
              Mode: transaction?.mode || "",
              Debit: type === "DEBIT" ? amount : "",
              Credit: type === "CREDIT" ? amount : "",
              Balance: Number(
                transaction?.transactionalBalance || 0
              ),
              "Transaction ID": transaction?.txnId || "",
            };
          });
          let sheetName =
            bankName
              ?.replace(/[\\/?*[\]:]/g, "")
              ?.substring(0, 31) || "Bank Statement";

          if (accountIndex > 0) {
            sheetName =
              `${sheetName.substring(0, 27)}_${accountIndex + 1}`;
          }
          const accountDetails = [
            ["BANK ACCOUNT STATEMENT"],
            [],
            ["Bank Name", bankName || ""],
            ["Branch Name", branchName],
            [],
            ["Account Holder Name", holder?.name || ""],
            ["Account Number", accountNumber],
            ["Account Type", accountType],
            ["IFSC Code", ifsc],
            ["Mobile Number", holder?.mobile || ""],
            [],
            [
              "Statement From",
              transactionsData?.startDate || "",
            ],
            [
              "Statement To",
              transactionsData?.endDate || "",
            ],
            [],
            ["TRANSACTION DETAILS"],
            [],
          ];

          const worksheet =
            XLSX.utils.aoa_to_sheet(accountDetails);
          XLSX.utils.sheet_add_json(
            worksheet,
            transactionRows,
            {
              origin: "A18",
              skipHeader: false,
            }
          );
          worksheet["!cols"] = [
            { wch: 22 }, // Date
            { wch: 18 }, // Value Date
            { wch: 55 }, // Description
            { wch: 25 }, // Reference
            { wch: 15 }, // Mode
            { wch: 18 }, // Debit
            { wch: 18 }, // Credit
            { wch: 20 }, // Balance
            { wch: 30 }, // Transaction ID
          ];
          worksheet["!merges"] = [
            {
              s: { r: 0, c: 0 },
              e: { r: 0, c: 8 },
            },
            {
              s: { r: 16, c: 0 },
              e: { r: 16, c: 8 },
            },
          ];
          // Main title
          if (worksheet["A1"]) {
            worksheet["A1"].s = {
              font: {
                bold: true,
                sz: 18,
                color: { rgb: "FFFFFF" },
              },
              fill: {
                fgColor: { rgb: "1F4E78" },
              },
              alignment: {
                horizontal: "center",
                vertical: "center",
              },
            };
          }

          // Transaction section title
          if (worksheet["A17"]) {
            worksheet["A17"].s = {
              font: {
                bold: true,
                sz: 13,
                color: { rgb: "FFFFFF" },
              },
              fill: {
                fgColor: { rgb: "4472C4" },
              },
              alignment: {
                horizontal: "center",
                vertical: "center",
              },
            };
          }
          const detailRows = [
            2, 3, 4, 6, 7, 8, 9, 10, 12, 13,
          ];

          detailRows.forEach((row) => {
            const labelCell = worksheet[`A${row + 1}`];
            const valueCell = worksheet[`B${row + 1}`];

            if (labelCell) {
              labelCell.s = {
                font: {
                  bold: true,
                },
                fill: {
                  fgColor: { rgb: "D9EAF7" },
                },
                alignment: {
                  vertical: "center",
                },
              };
            }

            if (valueCell) {
              valueCell.s = {
                alignment: {
                  vertical: "center",
                },
              };
            }
          });
          const transactionHeaderRow = 18;
          const headers = [
            "A",
            "B",
            "C",
            "D",
            "E",
            "F",
            "G",
            "H",
            "I",
          ];

          headers.forEach((column) => {
            const cell =
              worksheet[`${column}${transactionHeaderRow}`];

            if (cell) {
              cell.s = {
                font: {
                  bold: true,
                  color: { rgb: "FFFFFF" },
                },
                fill: {
                  fgColor: { rgb: "5B9BD5" },
                },
                alignment: {
                  horizontal: "center",
                  vertical: "center",
                },
              };
            }
          });
          const firstTransactionRow = 19;
          const lastTransactionRow =
            firstTransactionRow + transactionRows.length - 1;

          for (
            let row = firstTransactionRow;
            row <= lastTransactionRow;
            row++
          ) {
            // Debit
            const debitCell = worksheet[`F${row}`];

            if (debitCell && debitCell.v !== "") {
              debitCell.z = '#,##0.00';
              debitCell.s = {
                alignment: {
                  horizontal: "right",
                },
              };
            }

            // Credit
            const creditCell = worksheet[`G${row}`];

            if (creditCell && creditCell.v !== "") {
              creditCell.z = '#,##0.00';
              creditCell.s = {
                alignment: {
                  horizontal: "right",
                },
              };
            }

            // Balance
            const balanceCell = worksheet[`H${row}`];

            if (balanceCell) {
              balanceCell.z = '#,##0.00';
              balanceCell.s = {
                alignment: {
                  horizontal: "right",
                },
              };
            }

            // Date
            const dateCell = worksheet[`A${row}`];

            if (dateCell) {
              dateCell.s = {
                alignment: {
                  horizontal: "center",
                },
              };
            }
          }
          worksheet["!freeze"] = {
            xSplit: 0,
            ySplit: 18,
          };
          if (transactionRows.length > 0) {
            worksheet["!autofilter"] = {
              ref: `A18:I${lastTransactionRow}`,
            };
          }

          worksheet["!pageSetup"] = {
            orientation: "landscape",
            fitToWidth: 1,
            fitToHeight: 0,
          };

          XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            sheetName
          );
        });
      });
      XLSX.writeFile(
        workbook,
        "Bank_Statement.xlsx"
      );

    } catch (error) {
      console.error(
        "Error downloading bank statement:",
        error
      );
    }
  };

  return (
    <Paper style={styles.card}>
      <div style={styles.header}>
        <Typography style={styles.title}>
          Bank Details
        </Typography>

        {/* {bankAnalyzerDetails?.reportLink && ( */}
          <Button
            variant="contained"
            color="primary"
            onClick={downloadBankStatementExcel}
          >
            Download Bank Statement
          </Button>
        {/* )} */}
      </div>

      <Grid container spacing={2}>
        <Field
          label="Account Number"
          value={bankDetails?.bankAccountNo}
        />

        <Field
          label="IFSC Code"
          value={bankDetails?.ifscCode}
        />

        <Field
          label="Bank Name"
          value={toCamelCase(bankDetails?.bankName)}
        />

        <Field
          label="Beneficiary Name"
          value={toCamelCase(
            bankDetails?.beneficiaryName
          )}
        />

        <Field
          label="Branch Name"
          value={toCamelCase(bankDetails?.branch)}
        />

        <Field
          label="Branch Address"
          value={toCamelCase(
            bankDetails?.branchAddress
          )}
        />

        <Field
          label="Match Score"
          value={
            bankDetails?.matchScore
              ? `${bankDetails.matchScore}%`
              : "NA"
          }
        />
      </Grid>
    </Paper>
  );
};

Field.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any,
};

BankDetails.propTypes = {
  bankDetails: PropTypes.object,
  bankAnalyzerDetails: PropTypes.object,
};

export default BankDetails;